import * as Sentry from '@sentry/react';
import { useSyncExternalStore } from 'react';
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const RETURN_CHECK_MIN_AGE_MS = 60 * 1000;
const DEV_VERSION = 'dev';
export const VERSION_CHECK_STATUS = {
    NEW_VERSION: 'newVersion',
    SAME_VERSION: 'sameVersion',
    UNKNOWN: 'unknown',
};
const UNKNOWN_RESULT = { status: VERSION_CHECK_STATUS.UNKNOWN };
let watchedAppName = null;
let stopWatching = null;
let channel = null;
let stale = false;
let latestVersion;
let lastCheckTime = 0;
let inFlightCheck = null;
const staleListeners = new Set();
const unsavedForms = new Set();
function getRunningVersion() {
    return window['appVersion'];
}
// A local dev build runs as `dev`, which no deployed version file will ever match.
function isVersionCheckDisabled() {
    const runningVersion = getRunningVersion();
    return !runningVersion || runningVersion === DEV_VERSION;
}
function markStale(version) {
    latestVersion = version;
    if (stale) {
        return;
    }
    stale = true;
    staleListeners.forEach(listener => listener());
}
async function fetchLatestVersion() {
    try {
        const response = await fetch('/version.json', { cache: 'no-store' });
        if (!response.ok) {
            return undefined;
        }
        const body = await response.json();
        const version = body?.version;
        return typeof version === 'string' ? version : undefined;
    }
    catch {
        return undefined;
    }
}
function handleBroadcast(event) {
    const version = event.data?.version;
    if (typeof version === 'string' && version !== getRunningVersion()) {
        markStale(version);
    }
}
/**
 * Compares `/version.json` with the running version, marking this tab and the other open tabs stale on a mismatch.
 * Concurrent calls share one request.
 */
export function requestVersionCheck() {
    if (isVersionCheckDisabled()) {
        return Promise.resolve(UNKNOWN_RESULT);
    }
    if (!inFlightCheck) {
        lastCheckTime = Date.now();
        inFlightCheck = fetchLatestVersion().then((version) => {
            inFlightCheck = null;
            if (version === undefined) {
                return UNKNOWN_RESULT;
            }
            if (version === getRunningVersion()) {
                return { status: VERSION_CHECK_STATUS.SAME_VERSION };
            }
            markStale(version);
            channel?.postMessage({ version });
            return { status: VERSION_CHECK_STATUS.NEW_VERSION, version };
        });
    }
    return inFlightCheck;
}
/**
 * Checks for a new version every 5 minutes while the tab is visible and when the user returns to it, and listens to
 * the app's other tabs; call once from the entry.
 * @returns Stops watching and forgets the staleness state.
 */
export function startVersionWatch(appName) {
    if (stopWatching) {
        return stopWatching;
    }
    watchedAppName = appName;
    lastCheckTime = Date.now();
    let pollIntervalId;
    const startPolling = () => {
        if (pollIntervalId === undefined) {
            pollIntervalId = window.setInterval(() => void requestVersionCheck(), POLL_INTERVAL_MS);
        }
    };
    const stopPolling = () => {
        window.clearInterval(pollIntervalId);
        pollIntervalId = undefined;
    };
    const handleVisibilityChange = () => {
        if (document.visibilityState !== 'visible') {
            stopPolling();
            return;
        }
        if (Date.now() - lastCheckTime > RETURN_CHECK_MIN_AGE_MS) {
            void requestVersionCheck();
        }
        startPolling();
    };
    if (!isVersionCheckDisabled()) {
        if (typeof BroadcastChannel !== 'undefined') {
            channel = new BroadcastChannel(`${appName}-app-version`);
            channel.addEventListener('message', handleBroadcast);
        }
        document.addEventListener('visibilitychange', handleVisibilityChange);
        if (document.visibilityState === 'visible') {
            startPolling();
        }
    }
    stopWatching = () => {
        stopPolling();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        channel?.removeEventListener('message', handleBroadcast);
        channel?.close();
        channel = null;
        watchedAppName = null;
        stopWatching = null;
        latestVersion = undefined;
        lastCheckTime = 0;
        inFlightCheck = null;
        if (stale) {
            stale = false;
            staleListeners.forEach(listener => listener());
        }
    };
    return stopWatching;
}
export function isStale() {
    return stale;
}
export function subscribeToStaleness(listener) {
    staleListeners.add(listener);
    return () => {
        staleListeners.delete(listener);
    };
}
export function useIsStale() {
    return useSyncExternalStore(subscribeToStaleness, isStale, isStale);
}
/** Reports to Sentry a full load the user did not ask for, then performs it. */
export function forceFullLoad(reason, url) {
    Sentry.captureMessage('Forced full load', {
        level: 'info',
        tags: { reason },
        extra: { runningVersion: getRunningVersion(), latestVersion },
    });
    if (url) {
        window.location.assign(url);
    }
    else {
        window.location.reload();
    }
}
/**
 * Allows one automatic reload per target version per browser tab, so a reload that lands on an old server
 * during a rollout cannot loop.
 */
export function claimAutomaticReload(targetVersion) {
    if (!watchedAppName) {
        return false;
    }
    const storageKey = `${watchedAppName}-automatic-reload-version`;
    try {
        if (sessionStorage.getItem(storageKey) === targetVersion) {
            return false;
        }
        sessionStorage.setItem(storageKey, targetVersion);
        return true;
    }
    catch {
        // Without storage a reload loop cannot be ruled out.
        return false;
    }
}
/** Marks a form with unsaved changes as open until the returned function is called. */
export function registerUnsavedForm() {
    const formId = Symbol('unsavedForm');
    unsavedForms.add(formId);
    return () => {
        unsavedForms.delete(formId);
    };
}
export function isUnsavedFormOpen() {
    return unsavedForms.size > 0;
}
